import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Snackbar,
    Alert,
    IconButton,
    Box,
    Typography,
    Chip,
    CircularProgress,
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import InventoryIcon from '@mui/icons-material/Inventory';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import DataLoadingSpinner from 'components/Spinners/DataLoadingSpinner';
import NewMerchantAssetInventoryModal from 'components/NewMerchantAssetInventoryModal';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import AddressAutocompleteInput from 'layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput';
import { UiDetailSection, UiLabeledValue } from 'components/v2/record/DetailSection';


const formatSubtypeLabel = (value) =>
    String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());

const ASSET_SUBTYPES = {
    Accommodation: ['twin_share', 'dorm', 'private_room', 'villa'],
    Meal: ['breakfast', 'lunch', 'dinner', 'buffet', 'set_menu'],
    Meals: ['breakfast', 'lunch', 'dinner', 'buffet', 'set_menu'],
    Activity: ['guided_tour', 'ticket_entry', 'rental', 'trekking', 'paragliding', 'snorkeling', 'adventure'],
    Transport: ['bus_seat', 'private_car', 'shared_cab', 'boat', 'cruise'],
};

const MerchantAssetDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const { canEdit, canDelete, canCreate } = usePermission('merchant_assets');
    const [asset, setAsset] = useState(null);
    const [merchantsById, setMerchantsById] = useState({});
    const [tripLogisticsOptions, setTripLogisticsOptions] = useState([]);
    const [inventories, setInventories] = useState([]);
    const [isInventoriesLoading, setIsInventoriesLoading] = useState(false);
    const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [isUnauthorized, setIsUnauthorized] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchData();
        fetchMerchants();
        fetchTripLogistics();
        fetchInventories();
    }, [id]);

    const fetchData = async () => {
        setIsLoading(true);
        setIsUnauthorized(false);
        try {
            const response = await axiosInstance.get(`/merchant-assets/${id}`);
            setAsset(response.data);
        } catch (error) {
            console.error('Error fetching merchant asset details:', error);
            if (error.response?.status === 403) {
                setIsUnauthorized(true);
                setAsset(null);
                setSnackbar({
                    open: true,
                    message: "You don't have permission to access this Merchant Asset",
                    severity: 'error',
                });
                setTimeout(() => navigate('/merchant-assets'), 3000);
            } else {
                setSnackbar({ open: true, message: 'Error loading merchant asset details', severity: 'error' });
            }
        } finally {
            setIsLoading(false);
        }
    };

    const fetchInventories = async () => {
        if (!id) return;
        setIsInventoriesLoading(true);
        try {
            const response = await axiosInstance.get('/merchant-asset-inventories/', {
                params: { merchant_asset_id: id, limit: 100 },
            });
            setInventories(response.data || []);
        } catch (error) {
            console.error('Error fetching inventories:', error);
            setInventories([]);
        } finally {
            setIsInventoriesLoading(false);
        }
    };

    const handleSaveInventory = async (payload) => {
        await axiosInstance.post('/merchant-asset-inventories/', payload);
        setSnackbar({ open: true, message: 'Inventory created successfully', severity: 'success' });
        await fetchInventories();
    };

    const fetchMerchants = async () => {
        try {
            const response = await axiosInstance.get('/merchant-assets/merchant-options');
            const byId = (response.data || []).reduce((acc, merchant) => {
                acc[merchant.id] = merchant;
                return acc;
            }, {});
            setMerchantsById(byId);
        } catch (error) {
            console.error('Error loading merchants:', error);
        }
    };

    const fetchTripLogistics = async () => {
        try {
            const response = await axiosInstance.get('/merchant-assets/trip-logistics-options');
            setTripLogisticsOptions(response.data || []);
        } catch (error) {
            console.error('Error loading trip logistics:', error);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setAsset(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/merchant-assets/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Merchant Asset updated successfully', severity: 'success' });
            fetchData();
        } catch (error) {
            console.error('Error updating Merchant Asset:', error);
            setSnackbar({ open: true, message: 'Error updating Merchant Asset', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this Merchant Asset?')) {
            try {
                await axiosInstance.delete(`/merchant-assets/${id}`);
                setSnackbar({ open: true, message: 'Merchant Asset deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/merchant-assets'), 1000);
            } catch (error) {
                console.error('Error deleting Merchant Asset:', error);
                setSnackbar({ open: true, message: 'Error deleting Merchant Asset', severity: 'error' });
            }
        }
    };

    const handleUserClick = (userId, userName) => {
        if (userId) {
            navigate(`/users/${userId}/profile`);
        } else {
            setSnackbar({ open: true, message: `Unable to find user info for ${userName}.`, severity: 'info' });
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((item) => (
                    <Grid item xs={12} md={item.fullWidth ? 12 : 6} key={item.field}>
                        {['created_by_name', 'updated_by_name'].includes(item.field) ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={() => handleUserClick(item.field === 'created_by_name' ? asset.created_by : asset.updated_by, asset[item.field])}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {asset[item.field]}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : item.clickable ? (
                            <UiLabeledValue label={item.label}>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="regular" color="text">
                                    <strong>{item.label}:</strong>
                                </MDTypography>
                                <MDTypography
                                    component="span"
                                    onClick={item.onClick}
                                    variant="button"
                                    color="info"
                                    fontWeight="medium"
                                    sx={{ cursor: 'pointer', ml: 1 }}
                                >
                                    {item.renderValue ? item.renderValue(asset?.[item.field]) : asset?.[item.field]}
                                </MDTypography>
                            </MDBox>
                            </UiLabeledValue>
                        ) : item.field === 'geo_location' && isEditing ? (
                            <Box
                                sx={{
                                    '& .MuiOutlinedInput-root, & .MuiInputBase-root': {
                                        minHeight: 45,
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                <AddressAutocompleteInput
                                    label={item.label}
                                    placeholder="Enter geo location"
                                    value={updatedFields.geo_location ?? asset?.geo_location ?? ''}
                                    onChange={(nextLocation) => handleFieldUpdate('geo_location', nextLocation)}
                                />
                            </Box>
                        ) : (
                            <InlineEditField
                                label={item.label}
                                value={item.toEditValue ? item.toEditValue(asset?.[item.field]) : asset?.[item.field]}
                                onSave={(newValue) =>
                                    handleFieldUpdate(
                                        item.field,
                                        item.fromEditValue ? item.fromEditValue(newValue) : newValue
                                    )
                                }
                                isEditing={isEditing}
                                readOnly={item.readOnly || false}
                                renderValue={item.renderValue}
                                multiline={item.multiline}
                                type={item.type}
                                options={item.options}
                            />
                        )}
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox mb={3} p={3} borderRadius="lg" sx={{ backgroundColor: '#ffffff', boxShadow: 3 }}>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <DataLoadingSpinner
                    entityType="default"
                    title="Loading Merchant Asset"
                    message="Please wait while we fetch the merchant asset details..."
                />
                
            </DashboardLayout>
        );
    }

    if (!asset) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>
                        {isUnauthorized
                            ? "You don't have permission to access this Merchant Asset"
                            : 'Merchant Asset not found'}
                    </MDTypography>
                </MDBox>
            </DashboardLayout>
        );
    }

    const assetTypes = [
        { value: 'Accommodation', label: 'Accommodation' },
        { value: 'Meal', label: 'Meal' },
        { value: 'Meals', label: 'Meals' },
        { value: 'Activity', label: 'Activity' },
        { value: 'Transport', label: 'Transport' },
        { value: 'Transfer', label: 'Transfer' },
        { value: 'Guide', label: 'Guide' },
        { value: 'Rental', label: 'Rental' },
        { value: 'Other', label: 'Other' },
    ];
    const subtypeOptions = (ASSET_SUBTYPES[asset?.asset_type] || []).map((s) => ({
        value: s,
        label: formatSubtypeLabel(s),
    }));

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Card>
                            <MDBox
                                mx={2} mt={-3} py={3} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info"
                                display="flex" justifyContent="space-between" alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">{asset.name}</MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>Merchant Asset Details</MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {canEdit && (isEditing ? (
                                        <>
                                            <IconButton onClick={() => { setIsEditing(false); setUpdatedFields({}); fetchData(); }} sx={commonIconButtonStyles}><Close sx={commonIconStyles} /></IconButton>
                                            <IconButton onClick={handleSaveChanges} sx={commonIconButtonStyles}><Save sx={commonIconStyles} /></IconButton>
                                        </>
                                    ) : (
                                        <IconButton onClick={() => setIsEditing(true)} sx={commonIconButtonStyles}><Edit sx={commonIconStyles} /></IconButton>
                                    ))}
                                    {canDelete && (
                                        <IconButton onClick={handleDelete} sx={commonIconButtonStyles}><Delete sx={commonIconStyles} /></IconButton>
                                    )}
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Basic Information", [
                                    { label: "Asset Name", field: "name" },
                                    {
                                        label: "Merchant",
                                        field: "merchant_id",
                                        clickable: true,
                                        onClick: () => {
                                            if (asset?.merchant_id) {
                                                navigate(`/accounts/${asset.merchant_id}`);
                                            } else {
                                                setSnackbar({ open: true, message: 'Merchant account not found', severity: 'info' });
                                            }
                                        },
                                        renderValue: () => merchantsById[asset?.merchant_id]?.name || `#${asset?.merchant_id}`,
                                    },
                                    { label: "Asset Type", field: "asset_type", options: assetTypes },
                                    {
                                        label: "Subtype",
                                        field: "subtype",
                                        options: subtypeOptions,
                                        renderValue: (val) => (val ? String(val).replace(/_/g, ' ') : '-'),
                                    },
                                    {
                                        label: "Trip Logistics",
                                        field: "package_component_id",
                                        options: tripLogisticsOptions.map((item) => ({
                                            value: item.id,
                                            label: item.label || item.name,
                                        })),
                                        clickable: !isEditing && Boolean(asset?.package_component_id),
                                        onClick: () => {
                                            if (asset?.package_component_id) {
                                                navigate(`/trip-package-components/${asset.package_component_id}`);
                                            } else {
                                                setSnackbar({
                                                    open: true,
                                                    message: 'Trip logistics not linked',
                                                    severity: 'info',
                                                });
                                            }
                                        },
                                        renderValue: () =>
                                            asset?.trip_logistics_name
                                            || tripLogisticsOptions.find((item) => String(item.id) === String(asset?.package_component_id))?.label
                                            || (asset?.package_component_id ? `#${asset.package_component_id}` : '-'),
                                    },
                                    {
                                        label: "Status",
                                        field: "active",
                                        options: [
                                            { value: 'true', label: 'Active' },
                                            { value: 'false', label: 'Inactive' },
                                        ],
                                        toEditValue: (val) => (val === false ? 'false' : 'true'),
                                        fromEditValue: (val) => val !== 'false',
                                        renderValue: (val) => (val === 'false' ? 'Inactive' : 'Active'),
                                    },
                                    {
                                        label: "Inventory Controlled",
                                        field: "inventory_controlled",
                                        options: [
                                            { value: 'true', label: 'Yes' },
                                            { value: 'false', label: 'No' },
                                        ],
                                        toEditValue: (val) => (val === false ? 'false' : 'true'),
                                        fromEditValue: (val) => val !== 'false',
                                        renderValue: (val) => (val === 'false' ? 'No' : 'Yes'),
                                    },
                                ])}

                                {renderSection("Details & Location", [
                                    { label: "Capacity Model", field: "capacity_model", options: ['Per Pax', 'Per Room', 'Per Bed', 'Per Seat', 'Per Slot'] },
                                    { label: "Maximum Capacity per Unit", field: "capacity_per_slot", type: "number" },
                                    { label: "Cost Price", field: "cost_price", type: "number" },
                                    { label: "Rating", field: "rating", type: "number" },
                                    { label: "Geo Location", field: "geo_location", fullWidth: true },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={2}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <InventoryIcon sx={{ color: 'white !important', mr: 1 }} />
                                    <MDTypography variant="h6" color="white">
                                        Merchant Asset Inventory
                                    </MDTypography>
                                </Box>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    size="small"
                                    disabled={!canCreate || asset?.inventory_controlled === false}
                                    onClick={() => setIsInventoryModalOpen(true)}
                                >
                                    New
                                </MDButton>
                            </MDBox>
                            <MDBox p={2}>
                                {isInventoriesLoading ? (
                                    <Box sx={{ py: 3, textAlign: 'center' }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                ) : inventories.length === 0 ? (
                                    <Box sx={{ py: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No inventory configured yet.
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {asset?.inventory_controlled === false
                                                ? 'This asset is not inventory-controlled.'
                                                : 'Add date/slot inventory for this asset.'}
                                        </Typography>
                                    </Box>
                                ) : (
                                    inventories.map((inv) => (
                                        <Box
                                            key={inv.id}
                                            onClick={() => navigate(`/merchant-asset-inventory/${inv.id}`)}
                                            sx={{
                                                mb: 1,
                                                p: 1.5,
                                                cursor: 'pointer',
                                                borderRadius: '12px',
                                                border: '1px solid rgba(0,0,0,0.08)',
                                                '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="subtitle2" fontWeight="medium" color="info.main">
                                                    #{inv.id} · {inv.date ? new Date(inv.date).toLocaleDateString() : '-'}
                                                </Typography>
                                                <Chip
                                                    size="small"
                                                    label={inv.status || 'Available'}
                                                    variant="outlined"
                                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                                />
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Units {inv.available_units ?? '-'} / {inv.qty_total ?? '-'}
                                                {' · '}
                                                Cap {inv.available_capacity ?? '-'} / {inv.total_capacity ?? '-'}
                                            </Typography>
                                            {inv.time_slot && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Slot: {inv.time_slot}
                                                </Typography>
                                            )}
                                        </Box>
                                    ))
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <NewMerchantAssetInventoryModal
                isOpen={isInventoryModalOpen}
                onClose={() => setIsInventoryModalOpen(false)}
                onSave={handleSaveInventory}
                defaultMerchantAssetId={id}
                lockAsset
            />
            
            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantAssetDetail;
